import ApiService from '@/lib/ApiService'

export interface BusinessAdministrationResponse {
    businessAdministrationId: number
    businessAdministrationName: string
}

export interface UpdateBusinessAdministrationResponse {
    message?: string
    status?: number
    isSuccess?: boolean
}

export interface UsersToBARequest {
    businessAdministrationId?: number
    userId?: number
    userAccount?: string
}

export interface CreateBAUserRequest {
    userList: UsersToBARequest[]
}

let cachedBusinessAdministrations: BusinessAdministrationResponse[] | null =
    null
// In-flight BA-list request shared by concurrent callers. Several components
// fetch the list on mount at login (ModernLayout, DistributionMenu); the
// settled cache above is still empty at that point, so without this each of
// them issued its own identical network call.
let inFlightRequest: Promise<BusinessAdministrationResponse[]> | null = null

const ensureArray = (
    data: BusinessAdministrationResponse[] | unknown,
): BusinessAdministrationResponse[] => {
    if (Array.isArray(data)) {
        return data
    }
    return []
}

const setCache = (data: BusinessAdministrationResponse[]) => {
    cachedBusinessAdministrations = data.length ? [...data] : []
}

const requestAllBusinessAdministrations = async (): Promise<
    BusinessAdministrationResponse[]
> => {
    const response = await ApiService.fetchData<
        BusinessAdministrationResponse[]
    >({
        url: 'api/businessAdministration',
        method: 'get',
    })

    if (response instanceof Error) {
        cachedBusinessAdministrations = null
        throw response
    }

    if (!('data' in response)) {
        cachedBusinessAdministrations = null
        throw new Error('Unexpected API response shape')
    }

    const payload = ensureArray(response.data)
    setCache(payload)
    return payload
}

export const businessAdministrationService = {
    getAllBusinessAdministrations: async (
        forceRefresh = false,
    ): Promise<BusinessAdministrationResponse[]> => {
        if (!forceRefresh && cachedBusinessAdministrations) {
            return [...cachedBusinessAdministrations]
        }

        if (!forceRefresh && inFlightRequest) {
            return [...(await inFlightRequest)]
        }

        const request = requestAllBusinessAdministrations().finally(() => {
            // Identity check: a forceRefresh (or a call after clearCache) may
            // have replaced the tracked request — don't clear the newer one.
            if (inFlightRequest === request) {
                inFlightRequest = null
            }
        })
        inFlightRequest = request
        // Each caller gets its own copy so one consumer mutating the array
        // can't affect another — same contract as the settled-cache path.
        return [...(await request)]
    },

    updateBusinessAdministration: async (
        baId: number,
        userId: number,
    ): Promise<UpdateBusinessAdministrationResponse> => {
        const response =
            await ApiService.fetchData<UpdateBusinessAdministrationResponse>({
                url: `api/users/businessAdministration`,
                method: 'post',
                data: {
                    businessAdministrationId: baId,
                    userId: userId,
                },
            })

        if ('data' in response) {
            if (response.data?.isSuccess) {
                // Clear cache when BA is successfully updated
                cachedBusinessAdministrations = null
            }
            return response.data
        }
        cachedBusinessAdministrations = null
        throw new Error(
            response.message || 'Failed to update business administration',
        )
    },

    createBAUser: async (
        payload: CreateBAUserRequest,
    ): Promise<UpdateBusinessAdministrationResponse> => {
        const response =
            await ApiService.fetchData<UpdateBusinessAdministrationResponse>({
                url: 'api/businessAdministration/users',
                method: 'post',
                data: payload,
            })

        if ('data' in response) {
            return response.data
        }
        throw new Error(
            response.message || 'Failed to create BA user',
        )
    },

    deleteBAUser: async (
        baId: number,
        userId: number,
    ): Promise<UpdateBusinessAdministrationResponse> => {
        const response =
            await ApiService.fetchData<UpdateBusinessAdministrationResponse>({
                url: `api/businessAdministration/${baId}/users/${userId}`,
                method: 'delete',
            })

        if ('data' in response) {
            return response.data
        }
        throw new Error(
            response.message || 'Failed to delete BA user',
        )
    },

    deleteBA: async (
        baId: number,
    ): Promise<UpdateBusinessAdministrationResponse> => {
        const response =
            await ApiService.fetchData<UpdateBusinessAdministrationResponse>({
                url: `api/businessAdministration/${baId}`,
                method: 'delete',
            })

        if ('data' in response) {
            if (response.data?.isSuccess) {
                cachedBusinessAdministrations = null
            }
            return response.data
        }
        throw new Error(
            response.message || 'Failed to delete business administration',
        )
    },

    clearCache: () => {
        cachedBusinessAdministrations = null
        inFlightRequest = null
    },

    handleUserSignOut: () => {
        cachedBusinessAdministrations = null
        inFlightRequest = null
    },
}
